import { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, Panel, SectionHeader, Badge, EmptyState } from "@/components/ui/exec";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FolderTree, Layers, Boxes, ClipboardList, ScanText, Lock, ArrowRight, Activity, Plus, GraduationCap,
} from "lucide-react";

const FAM_LABEL = {
  material: "Materiales", mano_obra: "Mano de obra", herramienta: "Herramienta", equipo: "Equipo",
  seguridad_minera: "Seguridad minera", explosivos: "Explosivos", acero_barrenacion: "Acero de barrenación",
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export default function DashboardView({ go }) {
  const { user, company } = useAuth();
  const [data, setData] = useState(null);
  const allowed = new Set(user?.sections || []);

  useEffect(() => { if (user) api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [user]);

  const k = data?.kpis;
  const totalFam = (data?.insumos_por_familia || []).reduce((s, f) => s + f.n, 0) || 1;
  const quick = [
    { key: "presupuesto", label: "Nuevo presupuesto", icon: FolderTree },
    { key: "apu", label: "Analizar un APU", icon: Layers },
    { key: "estimaciones", label: "Registrar estimación", icon: ClipboardList },
    { key: "insumos", label: "Actualizar insumos", icon: Boxes },
    { key: "licitaciones", label: "Leer licitación con IA", icon: ScanText },
  ].filter((q) => allowed.has(q.key));

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden" data-testid="dashboard-view">
      <SectionHeader icon={LayoutDashboard} title="Panel ejecutivo" subtitle={`${company?.name || ""} · Resumen de proyectos, catálogos y actividad`}>
        <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1 text-xs" onClick={() => go("tutorial")} data-testid="dash-tutorial-btn"><GraduationCap className="h-3.5 w-3.5" /> Tutorial</Button>
        {allowed.has("presupuesto") && <Button size="sm" className="h-8 rounded-sm gap-1 text-xs" onClick={() => go("presupuesto")} data-testid="dash-new-presupuesto-btn"><Plus className="h-3.5 w-3.5" /> Presupuesto</Button>}
      </SectionHeader>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {!data && <div className="text-xs text-muted-foreground font-mono-num">Cargando indicadores…</div>}
        {data?.error && <EmptyState icon={Activity} title="No se pudo cargar el panel" text="Intenta recargar la página." />}
        {k && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              <KpiCard label="Monto presupuestado" value={k.total_contratado} money accent="hsl(36 96% 44%)" icon={FolderTree} testId="kpi-total" hint={`${k.presupuestos} presupuesto(s) · ${k.bloqueados} bloqueado(s)`} />
              <KpiCard label="Presupuestos" value={k.presupuestos} accent="hsl(212 92% 36%)" icon={FolderTree} testId="kpi-presupuestos" hint="Versiones activas y oficiales" />
              <KpiCard label="Conceptos APU" value={k.conceptos} accent="hsl(212 92% 36%)" icon={Layers} testId="kpi-conceptos" hint={`${k.basicos} básicos / auxiliares`} />
              <KpiCard label="Insumos en catálogo" value={k.insumos} accent="hsl(215 16% 47%)" icon={Boxes} testId="kpi-insumos" hint="Materiales, MO, equipo, explosivos…" />
              <KpiCard label="Avance estimado" value={k.avance_pct} unit="%" accent="hsl(160 84% 30%)" icon={ClipboardList} testId="kpi-avance" hint={`$ ${fmt(k.estimado_acumulado, 2)} en ${k.estimaciones} estimación(es)`} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              <Panel title="Presupuestos recientes" icon={FolderTree} className="lg:col-span-2 overflow-hidden" testId="dash-recientes"
                right={allowed.has("presupuesto") && <button className="text-[10px] text-primary normal-case tracking-normal flex items-center gap-1 hover:underline" onClick={() => go("presupuesto")} data-testid="dash-ver-presupuestos">Ver todos <ArrowRight className="h-3 w-3" /></button>}>
                {data.presupuestos_recientes.length === 0 ? (
                  <EmptyState icon={FolderTree} title="Sin presupuestos" text="Crea tu primer presupuesto para ver indicadores aquí."
                    action={allowed.has("presupuesto") && <Button size="sm" className="rounded-sm h-8" onClick={() => go("presupuesto")}>Crear presupuesto</Button>} />
                ) : (
                  <table className="qc-table">
                    <thead><tr><th>Proyecto</th><th>Cliente</th><th className="text-right" style={{ width: 150 }}>Total</th><th style={{ width: 110 }}>Estado</th></tr></thead>
                    <tbody>
                      {data.presupuestos_recientes.map((p) => (
                        <tr key={p.id} className="cursor-pointer" onClick={() => go("presupuesto", p.id)} data-testid={`dash-pres-${p.id}`}>
                          <td className="text-xs font-medium">{p.nombre}</td>
                          <td className="text-xs text-muted-foreground">{p.cliente || "—"}</td>
                          <td className="text-right font-mono-num">$ {fmt(p.total, 2)}</td>
                          <td>{p.bloqueado ? <Badge tone="success"><Lock className="h-2.5 w-2.5" /> Oficial v{p.version}</Badge> : <Badge tone="warning">Borrador v{p.version}</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>

              <div className="space-y-4">
                <Panel title="Accesos rápidos" icon={Activity} testId="dash-quick">
                  <div className="p-2 grid grid-cols-1 gap-1">
                    {quick.map((q) => {
                      const I = q.icon;
                      return (
                        <button key={q.key} onClick={() => go(q.key)} className="flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs text-left hover:bg-primary/5 hover:text-primary transition-colors" data-testid={`dash-quick-${q.key}`}>
                          <I className="h-4 w-4 text-copper" /> {q.label} <ArrowRight className="h-3 w-3 ml-auto opacity-40" />
                        </button>
                      );
                    })}
                  </div>
                </Panel>
                <Panel title="Catálogo de insumos por familia" icon={Boxes} testId="dash-familias">
                  <div className="p-3 space-y-2">
                    {data.insumos_por_familia.map((f) => (
                      <div key={f.familia}>
                        <div className="flex justify-between text-[11px] mb-0.5"><span>{FAM_LABEL[f.familia] || f.familia}</span><span className="font-mono-num text-muted-foreground">{f.n}</span></div>
                        <div className="h-1.5 bg-muted rounded-sm"><div className="h-1.5 bg-primary rounded-sm transition-all duration-500" style={{ width: `${(f.n / totalFam) * 100}%` }} /></div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>

            <Panel title="Actividad reciente" icon={Activity} testId="dash-actividad">
              {data.actividad.length === 0 ? <div className="p-4 text-xs text-muted-foreground">Sin actividad registrada.</div> : (
                <ul className="divide-y divide-border/60">
                  {data.actividad.map((a) => (
                    <li key={a.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} />
                      <span className="font-mono-num text-[10px] text-muted-foreground w-28 shrink-0">{fmtDate(a.created_at)}</span>
                      <span className="truncate flex-1">{a.detail || a.action}</span>
                      <span className="text-[10px] text-muted-foreground truncate max-w-[180px]">{a.email || a.actor_email || ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}
